import { useAppSelector } from '../../../app/hooks';
import {
  CubeMeasureEnum,
  selectColorsDistributionMergeFactor,
} from '../../../services/data/impl/cube/cubeSlice';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import {
  selectCurrMapColorBucket,
  selectHueRotationOffset,
} from '../../map/mapSlice';
import { selectCurrMeasure } from '../../select/selectSlice';
import { ScanDataEnum } from '../scanDataDefinitions';
import {
  ValidPointsDropdownOptions,
  selectShowValidPointsDropdown,
} from '../scanDataSlice';
import { IntervalValue } from './useScanDataDistribution';
import { OperatorEnum, useScanDataQuery } from './useScanDataQuery';

export enum StdDevFilterEnum {
  class_name = ScanDataEnum.class_name,
  model_name = ScanDataEnum.model_name,
  model_version = ScanDataEnum.model_version,
}

export interface ColorDistributionQueryResult extends IntervalValue {
  h: number;
  s: number;
  v: number;
}

export const useScanDataColorDistribution = ({
  skip = false,
}: {
  skip?: boolean;
}): DuckQueryReturnListType<ColorDistributionQueryResult> => {
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currMapColorBucket = useAppSelector(selectCurrMapColorBucket);
  const colorsMergeFactor = useAppSelector(selectColorsDistributionMergeFactor);
  const hueRotationOffset = useAppSelector(selectHueRotationOffset);
  const showValidPoints = useAppSelector(selectShowValidPointsDropdown);
  const cols = [
    `FLOOR(FLOOR((${ScanDataEnum.color_hsv}::float[])[1] * 360) / ${colorsMergeFactor}) * ${colorsMergeFactor} as h`, // hue
    `AVG((${ScanDataEnum.color_hsv}::float[])[2]) * 100 as s`, // saturation
    `AVG((${ScanDataEnum.color_hsv}::float[])[3]) * 100 as v`, // value
    `COUNT(*) as interval_value`,
  ];

  const filters = [];

  if (currMapColorBucket) {
    filters.push({
      col: 'h',
      op: OperatorEnum.gte,
      value: currMapColorBucket[0],
    });
    filters.push({
      col: 'h',
      op: OperatorEnum.lte,
      value: currMapColorBucket[1],
    });
  }

  if (showValidPoints === ValidPointsDropdownOptions.showValidPoints) {
    filters.push({
      col: ScanDataEnum.color_hsv,
      op: OperatorEnum.neq,
      value: null,
    });
  }

  if (showValidPoints === ValidPointsDropdownOptions.showInvalidPoints) {
    filters.push({
      col: ScanDataEnum.color_hsv,
      op: OperatorEnum.eq,
      value: null,
    });
  }

  const group = ['h'];
  const order: [string, 'asc' | 'desc'][] = [
    [
      `CASE WHEN h - ${hueRotationOffset} < 0
      THEN h - ${hueRotationOffset} + 360
      ELSE h - ${hueRotationOffset}
      END
    `,
      'asc',
    ],
  ];
  const scanDistributionFilteredByFeature =
    useScanDataQuery<ColorDistributionQueryResult>({
      cols,
      group,
      order,
      useUnitsTransformSubstringMatching: true,
      filters,
      skip: skip || currMeasure !== CubeMeasureEnum.DetectionFactHasColorHSV,
    });

  return scanDistributionFilteredByFeature;
};
